import { forwardRef, type HTMLAttributes } from 'react';
import { cn } from '@/lib/utils';

type Variant = 'solid' | 'glass' | 'outline';

interface CardProps extends HTMLAttributes<HTMLDivElement> {
  variant?: Variant;
  interactive?: boolean;
}

/**
 * Vitt kort mot den lavendeltonade grunden. Formen bärs av radien och en
 * hårlinje; skuggan är nästan omärklig och finns bara för att lyfta kortet
 * en aning från ytan.
 *
 * `glass` finns kvar som namn för att inte tvinga fram ändringar på varje
 * anropsplats, men ritar samma kort som `solid`.
 */
const variants: Record<Variant, string> = {
  solid: 'bg-surface border border-ink-200 shadow-card',
  glass: 'bg-surface border border-ink-200 shadow-card',
  outline: 'bg-surface border border-ink-300',
};

export const Card = forwardRef<HTMLDivElement, CardProps>(
  ({ variant = 'solid', interactive, className, ...rest }, ref) => (
    <div
      ref={ref}
      className={cn(
        'rounded-xl',
        variants[variant],
        interactive &&
          'transition-[border-color,box-shadow] duration-150 hover:border-ink-300 hover:shadow-card-lg cursor-pointer',
        className,
      )}
      {...rest}
    />
  ),
);

Card.displayName = 'Card';
